import type { ApolloError } from '@apollo/client';
import { useBackToExamRoom, useCheckExamStatusByCardId } from '@exam-room/graphql';
import { generatePath, Navigate, Outlet, useNavigate } from 'react-router-dom';

import { ExamLoadingScreen, Page } from '../../../components';
import { useExamCard, useExamEvent, useNetwork } from '../../../providers';
import { PATHS } from '../../../routes';

// Main component for all routes under `/exam`
export function ProtectedExamRoute() {
  const { examCard, loading: isExamCardLoading } = useExamCard();
  const navigate = useNavigate();

  const [backToExamRoom, { loading: isBackToExamRoomLoading }] = useBackToExamRoom({
    variables: {
      input: {
        examCardId: examCard?.id ?? '',
        isReset: false,
      },
    },
  });

  const { loading: isCheckExamStatusLoading } = useCheckExamStatusByCardId({
    variables: {
      examCardId: examCard?.id ?? '',
    },
    fetchPolicy: 'network-only',
    skip: !examCard?.id || isExamCardLoading,
    onCompleted: (data) => {
      if (data.erCheckExamStatusByExamCardId?.id) {
        backToExamRoom();
      } else {
        // navigate back to instruction page
        navigate(generatePath(PATHS.Exam.Instruction, { examCardId: examCard?.id ?? '' }));
      }
    },
    onError: () => {
      // navigate back to instruction page
      navigate(generatePath(PATHS.Exam.Instruction, { examCardId: examCard?.id ?? '' }));
    },
  });

  if (isCheckExamStatusLoading || isBackToExamRoomLoading) {
    return <ExamLoadingScreen />;
  }

  // All routes under `/exam` will be inside `<Page />`
  return (
    <Page containerClassName='flex flex-col'>
      <Outlet />
    </Page>
  );
}

interface ProtectedExamEventRouteProps {
  children: React.ReactNode;
}

export function ProtectedExamEventRoute({ children }: Readonly<ProtectedExamEventRouteProps>) {
  const { loading: isLoadingExamEvent, error: examEventError, examDurationError } = useExamEvent();
  const { loading: isLoadingExamCard, error: examCardError } = useExamCard();
  const { isOnline } = useNetwork();

  if (isLoadingExamEvent || isLoadingExamCard) {
    return <ExamLoadingScreen />;
  }

  if (examEventError || examCardError || examDurationError) {
    if (isOnline) {
      const hasNetworkError =
        isNetworkError(examEventError) ||
        isNetworkError(examCardError) ||
        isNetworkError(examDurationError);
      if (!hasNetworkError) {
        return <Navigate replace to={PATHS.Home} />;
      }
    }
  }

  return children;
}

function isNetworkError(error: Error | undefined): boolean {
  if (!error) {
    return false;
  }

  const apolloError = error as ApolloError;
  if ('networkError' in apolloError && apolloError.networkError) {
    return true;
  }

  if ('graphQLErrors' in apolloError && apolloError.graphQLErrors.length === 0 && error.message) {
    return checkNetworkErrorPatterns(error.message);
  }

  if (error.message) {
    return checkNetworkErrorPatterns(error.message);
  }

  return false;
}

function checkNetworkErrorPatterns(message: string): boolean {
  const networkErrorPatterns = [
    /failed to fetch/i,
    /network\s*(request\s*)?failed/i,
    /net::err_/i,
    /networkerror/i,
    /timeout/i,
    /econnrefused/i,
    /enotfound/i,
    /socket hang up/i,
    /load failed/i, // Safari
    /the internet connection appears to be offline/i,
  ];
  return networkErrorPatterns.some((pattern) => pattern.test(message));
}
